import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectProperties, selectCredits, selectMembers, selectBanks } from '@/utils/store';
import { CATEGORY_ICONS } from '@/utils/format';
import { useCurrency } from '@/hooks/useCurrency';
import PageHeader from '@/components/shared/PageHeader';
import ViewToggle from '@/components/ui/ViewToggle';
import AddPropertyModal from '@/components/shared/AddPropertyModal';
import AddTransactionModal from '@/components/shared/AddTransactionModal';
import CollaboratorsPanel from '@/components/shared/CollaboratorsPanel';
import { Plus, ChevronLeft, ChevronRight, Trash2, Pencil, ArrowLeft, User, Phone, CreditCard, Zap, Droplets, Building, MoreHorizontal, Check, Clock, Copy } from 'lucide-react';

const STATUS_COLORS = {
  available: 'bg-success-100 text-success-700 dark:bg-success-500/10 dark:text-success-400',
  rented: 'bg-brand-100 text-brand-700 dark:bg-brand-500/10 dark:text-brand-400',
  maintenance: 'bg-warning-100 text-warning-700 dark:bg-warning-500/10 dark:text-warning-400',
};

const STATUSES = ['available', 'rented', 'maintenance'];

export default function Properties() {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const properties = useStore(selectProperties);
  const credits = useStore(selectCredits);
  const transactions = useStore((s) => s.transactions);
  const banks = useStore(selectBanks);
  const removeProperty = useStore((s) => s.removeProperty);
  const updateProperty = useStore((s) => s.updateProperty);
  const duplicateProperty = useStore((s) => s.duplicateProperty);
  const setTenantExpense = useStore((s) => s.setTenantExpense);
  const allTenantExpenses = useStore((s) => s.tenantExpenses);
  const members = useStore(selectMembers);

  const [modalOpen, setModalOpen] = useState(false);
  const [editProperty, setEditProperty] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const [viewMode, setViewMode] = useState('list');

  // Transaction modal state
  const [txModalOpen, setTxModalOpen] = useState(false);
  const [txDefaultType, setTxDefaultType] = useState('income');
  const [editingTx, setEditingTx] = useState(null);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [expenseAmount, setExpenseAmount] = useState('');

  const now = new Date();
  const viewDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthKey = viewDate.toISOString().slice(0, 7);
  const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const shiftMonth = (dir) => setMonthOffset((p) => p + dir);

  const selected = properties.find((p) => p.id === selectedId);

  const getCredit = (creditId) => credits.find((c) => c.id === creditId);
  const getMember = (memberId) => members.find((m) => m.id === memberId);
  const getBankName = (bankId) => banks.find((b) => b.id === bankId)?.name || '';

  const handleEdit = (prop) => {
    setEditProperty(prop);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditProperty(null);
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    removeProperty(id);
    if (selectedId === id) setSelectedId(null);
  };

  const openTxModal = (type) => {
    setTxDefaultType(type);
    setTxModalOpen(true);
  };

  // Property card component (shared between list and kanban)
  const PropertyCard = ({ prop, index, compact = false }) => {
    const credit = prop.creditId ? getCredit(prop.creditId) : null;
    const mortgage = credit ? credit.installmentAmount : 0;
    const profit = prop.rentAmount - mortgage;
    const statusKey = prop.status || 'available';

    return (
      <div
        className="card hover:border-brand-300 dark:hover:border-brand-600 transition-colors cursor-pointer group relative"
        onClick={() => setSelectedId(prop.id)}
      >
        <div className={compact ? 'space-y-2' : 'flex items-center gap-3'}>
          <div className={compact ? 'flex items-center gap-2' : 'flex items-center gap-3 flex-1 min-w-0'}>
            <div className="relative flex-shrink-0">
              <div
                className={`${compact ? 'w-9 h-9 text-lg' : 'w-11 h-11 text-xl'} rounded-xl flex items-center justify-center`}
                style={{ backgroundColor: prop.color + '20' }}
              >
                {prop.icon}
              </div>
              <span className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-brand-600 text-white text-[9px] font-bold flex items-center justify-center">
                {index + 1}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`font-semibold text-gray-900 dark:text-white ${compact ? 'text-xs' : 'text-sm'} truncate`}>{prop.name}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md shrink-0 ${STATUS_COLORS[statusKey]}`}>
                  {t(`properties.status${statusKey.charAt(0).toUpperCase() + statusKey.slice(1)}`)}
                </span>
              </div>
              {prop.address && (
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">{prop.address}</span>
              )}
            </div>
          </div>
          {/* Financial info */}
          <div className={compact ? 'flex items-center justify-between' : 'text-right'}>
            <div className="flex items-center gap-1">
              {prop.memberIds?.length > 0 && (
                <div className="flex items-center gap-0.5">
                  {prop.memberIds.map((id) => {
                    const owner = getMember(id);
                    if (!owner) return null;
                    return owner.emoji ? (
                      <span key={id} className="w-4 h-4 rounded-full flex items-center justify-center text-[8px]" style={{ backgroundColor: owner.color + '20' }}>
                        {owner.emoji}
                      </span>
                    ) : (
                      <span key={id} className="w-4 h-4 rounded-full flex items-center justify-center text-[7px] font-bold text-white" style={{ backgroundColor: owner.color }}>
                        {owner.initials}
                      </span>
                    );
                  })}
                </div>
              )}
              {!compact && prop.tenantName && (
                <div className="flex items-center gap-1">
                  <User size={10} className="text-gray-400" />
                  <span className="text-[10px] text-gray-400">{prop.tenantName}</span>
                </div>
              )}
            </div>
            <div className={compact ? 'text-right' : ''}>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <span className="text-success-600 dark:text-success-400">+{formatCurrency(prop.rentAmount)}</span>
                {mortgage > 0 && (
                  <>
                    {' / '}
                    <span className="text-danger-600 dark:text-danger-400">-{formatCurrency(mortgage)}</span>
                  </>
                )}
              </div>
              <div className={`text-xs font-bold ${profit >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
                {formatCurrency(profit)}
              </div>
            </div>
          </div>
        </div>

        {/* Hover actions */}
        <div className="absolute top-2 right-2 hidden group-hover:flex gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); duplicateProperty(prop.id); }}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-brand-600 transition-colors"
          >
            <Copy size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(prop); }}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-brand-600 transition-colors"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(prop.id); }}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-danger-500 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    );
  };

  // Detail view
  if (selected) {
    const propTx = transactions.filter((tx) => tx.propertyId === selected.id && tx.date?.startsWith(monthKey));
    const propIncome = propTx.filter((tx) => tx.type === 'income').reduce((s, tx) => s + tx.amount, 0);
    const propExpenses = propTx.filter((tx) => tx.type === 'expense').reduce((s, tx) => s + tx.amount, 0);

    const credit = selected.creditId ? getCredit(selected.creditId) : null;
    const mortgageAmount = credit ? credit.installmentAmount : 0;
    const monthlyProfit = selected.rentAmount - mortgageAmount;
    const propIndex = properties.findIndex((p) => p.id === selected.id);

    return (
      <div className="space-y-4 animate-fade-in">
        <button
          onClick={() => setSelectedId(null)}
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
        >
          <ArrowLeft size={16} />
          {t('properties.title')}
        </button>

        {/* Property header */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
              style={{ backgroundColor: selected.color + '20' }}
            >
              {selected.icon}
            </div>
            <span className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-brand-600 text-white text-[9px] font-bold flex items-center justify-center">
              {propIndex + 1}
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-display font-bold text-gray-900 dark:text-white">{selected.name}</h1>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${STATUS_COLORS[selected.status || 'available']}`}>
                {t(`properties.status${(selected.status || 'available').charAt(0).toUpperCase() + (selected.status || 'available').slice(1)}`)}
              </span>
            </div>
            {selected.address && (
              <span className="text-sm text-gray-500 dark:text-gray-400">{selected.address}</span>
            )}
            {selected.memberIds?.length > 0 && (
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                {selected.memberIds.map((id) => {
                  const owner = getMember(id);
                  if (!owner) return null;
                  return (
                    <span key={id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: owner.color + '20', color: owner.color }}>
                      {owner.emoji || owner.initials} {owner.name}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <button
            onClick={() => handleEdit(selected)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
          >
            <Pencil size={16} />
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('properties.monthlyRent')}</div>
            <div className="text-lg font-bold text-success-600 dark:text-success-400">{formatCurrency(selected.rentAmount)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('properties.monthlyMortgage')}</div>
            <div className="text-lg font-bold text-danger-600 dark:text-danger-400">{formatCurrency(mortgageAmount)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{t('properties.monthlyProfit')}</div>
            <div className={`text-lg font-bold ${monthlyProfit >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
              {formatCurrency(monthlyProfit)}
            </div>
          </div>
        </div>

        {/* Tenant info */}
        <div className="card">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{t('properties.tenant')}</div>
          {selected.tenantName ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                <User size={14} className="text-gray-400" />
                {selected.tenantName}
              </div>
              {selected.tenantContact && (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Phone size={14} className="text-gray-400" />
                  {selected.tenantContact}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-400 dark:text-gray-500">{t('properties.noTenant')}</div>
          )}
        </div>

        {/* Tenant expense tracking */}
        {selected.tenantName && (() => {
          const EXPENSE_TYPES = ['electricity', 'water', 'common', 'other'];
          const EXPENSE_ICONS = { electricity: Zap, water: Droplets, common: Building, other: MoreHorizontal };
          const monthExpenses = allTenantExpenses.filter(
            (te) => te.propertyId === selected.id && te.month === monthKey
          );
          const getExpense = (type) => monthExpenses.find((te) => te.type === type);

          return (
            <div className="card">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">{t('properties.tenantExpenses')}</div>
              <div className="space-y-2">
                {EXPENSE_TYPES.map((type) => {
                  const expense = getExpense(type);
                  const isPaid = expense?.paid;
                  const Icon = EXPENSE_ICONS[type];
                  const isEditing = editingExpenseId === `${type}-${monthKey}`;

                  return (
                    <div key={type} className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setTenantExpense(selected.id, monthKey, type, {
                            paid: !isPaid,
                            amount: expense?.amount || 0,
                          });
                        }}
                        className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                          isPaid
                            ? 'bg-success-100 dark:bg-success-500/20 text-success-600 dark:text-success-400'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                        }`}
                      >
                        {isPaid ? <Check size={14} /> : <Clock size={14} />}
                      </button>
                      <Icon size={16} className="text-gray-400 flex-shrink-0" />
                      <span className={`text-sm flex-1 ${isPaid ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                        {t(`properties.expenseTypes.${type}`)}
                      </span>
                      {isEditing ? (
                        <input
                          type="number"
                          className="input w-24 text-right text-sm py-1 px-2"
                          value={expenseAmount}
                          onChange={(e) => setExpenseAmount(e.target.value)}
                          onBlur={() => {
                            const amt = parseFloat(expenseAmount) || 0;
                            setTenantExpense(selected.id, monthKey, type, {
                              paid: isPaid || false,
                              amount: amt,
                            });
                            setEditingExpenseId(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.target.blur();
                          }}
                          autoFocus
                        />
                      ) : (
                        <button
                          onClick={() => {
                            setEditingExpenseId(`${type}-${monthKey}`);
                            setExpenseAmount(expense?.amount ? String(expense.amount) : '');
                          }}
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                        >
                          {expense?.amount ? formatCurrency(expense.amount) : '$0'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {monthExpenses.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    {monthExpenses.filter((te) => te.paid).length}/{EXPENSE_TYPES.length} {t('properties.paid').toLowerCase()}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(monthExpenses.reduce((s, te) => s + (te.amount || 0), 0))}
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Collaborators */}
        <CollaboratorsPanel resourceType="property" resourceId={selected.id} />

        {/* Mortgage info */}
        <div className="card">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{t('properties.mortgage')}</div>
          <div className="flex gap-2 flex-wrap mb-2">
            <button
              onClick={() => updateProperty(selected.id, { creditId: undefined })}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                !selected.creditId
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-brand-400'
              }`}
            >
              {t('properties.noMortgage')}
            </button>
            {credits.map((c) => (
              <button
                key={c.id}
                onClick={() => updateProperty(selected.id, { creditId: c.id })}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  selected.creditId === c.id
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                }`}
              >
                {c.description}
                {c.bankId && (
                  <span className="text-gray-400">({getBankName(c.bankId)})</span>
                )}
              </button>
            ))}
          </div>
          {credit && (
            <>
              <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                <CreditCard size={14} className="text-gray-400" />
                {formatCurrency(credit.installmentAmount)}/mes
                <span className="text-xs text-gray-400">
                  · {t('credits.installmentOf', { current: credit.paidInstallments, total: credit.totalInstallments })}
                </span>
              </div>
              {credit.totalInstallments > 0 && (
                <div className="mt-2">
                  <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-500 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (credit.paidInstallments / credit.totalInstallments) * 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                    <span>{Math.round((credit.paidInstallments / credit.totalInstallments) * 100)}%</span>
                    <span>{credit.paidInstallments}/{credit.totalInstallments}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Quick add transaction buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => openTxModal('income')}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400 text-sm font-medium hover:bg-success-100 dark:hover:bg-success-500/20 transition-colors border border-success-200 dark:border-success-500/20"
          >
            <Plus size={16} />
            {t('common.income')}
          </button>
          <button
            onClick={() => openTxModal('expense')}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400 text-sm font-medium hover:bg-danger-100 dark:hover:bg-danger-500/20 transition-colors border border-danger-200 dark:border-danger-500/20"
          >
            <Plus size={16} />
            {t('common.expense')}
          </button>
        </div>

        {/* Month nav */}
        <div className="flex items-center justify-between">
          <button onClick={() => shiftMonth(-1)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
            <ChevronLeft size={20} />
          </button>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{monthLabel}</span>
          <button onClick={() => shiftMonth(1)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Month totals */}
        {(propIncome > 0 || propExpenses > 0) && (
          <div className="grid grid-cols-2 gap-3">
            <div className="card text-center py-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('common.income')}</div>
              <div className="text-lg font-bold text-success-600 dark:text-success-400">{formatCurrency(propIncome)}</div>
            </div>
            <div className="card text-center py-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('common.expense')}</div>
              <div className="text-lg font-bold text-danger-600 dark:text-danger-400">{formatCurrency(propExpenses)}</div>
            </div>
          </div>
        )}

        {/* Transactions */}
        <div>
          <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{t('properties.transactions')}</h2>
          {propTx.length === 0 ? (
            <div className="card text-center py-6 text-sm text-gray-400 dark:text-gray-500">
              {t('properties.noTransactions')}
            </div>
          ) : (
            <div className="space-y-2">
              {propTx.map((tx) => (
                <div key={tx.id} onClick={() => setEditingTx(tx)} className="card flex items-center gap-3 py-3 px-4 cursor-pointer hover:border-brand-300 dark:hover:border-brand-600 transition-colors">
                  <span className="text-lg">{CATEGORY_ICONS[tx.category] || '📌'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{tx.description}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{t(`categories.${tx.category}`)} · {tx.date}</div>
                  </div>
                  <span className={`text-sm font-bold ${tx.type === 'income' ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
                    {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <AddPropertyModal open={modalOpen} onClose={() => setModalOpen(false)} property={editProperty} />
        <AddTransactionModal open={txModalOpen} onClose={() => setTxModalOpen(false)} defaultType={txDefaultType} defaultPropertyId={selected.id} />
        <AddTransactionModal open={!!editingTx} onClose={() => setEditingTx(null)} transaction={editingTx} />
      </div>
    );
  }

  // List / Kanban view
  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={t('properties.title')}
        hint={t('common.hints.properties')}
        action={
          <div className="flex items-center gap-2">
            <ViewToggle view={viewMode} onChange={setViewMode} />
            <button
              onClick={handleAdd}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
            >
              <Plus size={16} />
              {t('properties.addNew')}
            </button>
          </div>
        }
      />

      {/* Summary card */}
      {properties.length > 0 && (() => {
        const totalRent = properties.reduce((s, p) => s + (p.rentAmount || 0), 0);
        const totalMortgage = properties.reduce((s, p) => {
          const c = p.creditId ? getCredit(p.creditId) : null;
          return s + (c ? c.installmentAmount : 0);
        }, 0);
        return (
          <div className="grid grid-cols-3 gap-3">
            <div className="card text-center py-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('properties.monthlyRent')}</div>
              <div className="text-lg font-bold text-success-600 dark:text-success-400">{formatCurrency(totalRent)}</div>
              <div className="text-[10px] text-gray-400">{properties.length} {t('properties.title').toLowerCase()}</div>
            </div>
            <div className="card text-center py-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('properties.monthlyMortgage')}</div>
              <div className="text-lg font-bold text-danger-600 dark:text-danger-400">{formatCurrency(totalMortgage)}</div>
            </div>
            <div className="card text-center py-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">{t('properties.monthlyProfit')}</div>
              <div className={`text-lg font-bold ${totalRent - totalMortgage >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
                {formatCurrency(totalRent - totalMortgage)}
              </div>
            </div>
          </div>
        );
      })()}

      {properties.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-4xl mb-3">🏢</div>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
            {t('properties.noProperties')}
          </p>
          <button
            onClick={handleAdd}
            className="mt-4 px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            {t('properties.addNew')}
          </button>
        </div>
      ) : viewMode === 'list' ? (
        <div className="space-y-3">
          {properties.map((prop, i) => (
            <PropertyCard key={prop.id} prop={prop} index={i} />
          ))}
        </div>
      ) : (
        // Kanban view
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
          {STATUSES.map((status) => {
            const items = properties.filter((p) => (p.status || 'available') === status);
            return (
              <div key={status} className="flex-shrink-0 w-72 md:flex-1 md:min-w-0">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${STATUS_COLORS[status]}`}>
                    {t(`properties.status${status.charAt(0).toUpperCase() + status.slice(1)}`)}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">{items.length}</span>
                </div>
                <div className="space-y-2 min-h-[100px] bg-gray-50 dark:bg-gray-900/50 rounded-xl p-2">
                  {items.length === 0 ? (
                    <div className="text-center py-8 text-xs text-gray-400 dark:text-gray-500">
                      {t('common.columnEmpty')}
                    </div>
                  ) : (
                    items.map((prop) => {
                      const globalIndex = properties.findIndex((p) => p.id === prop.id);
                      return <PropertyCard key={prop.id} prop={prop} index={globalIndex} compact />;
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddPropertyModal open={modalOpen} onClose={() => setModalOpen(false)} property={editProperty} />
    </div>
  );
}
